import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import TextPlaceholder from 'Component/TextPlaceholder';
import { SKELETON_PRICE } from 'Route/ProductPage/ProductPageSkeleton.config';
import { MixType } from 'Type/Common.type';
import { OriginalPriceType, ProductPriceType } from 'Type/Price.type';
import { PriceConfiguration } from 'Type/ProductList.type';
import PRODUCT_TYPE from 'Util/Product/Product.config';

import SkeletonComponent from '../Skeleton/Skeleton.component';

/** @namespace Bodypwa/Component/ProductPrice/Component */
export class ProductPriceComponent extends PureComponent {
    static propTypes = {
        price: ProductPriceType,
        priceType: PropTypes.oneOf(Object.values(PRODUCT_TYPE)),
        originalPrice: OriginalPriceType,
        tierPrice: PropTypes.string,
        configuration: PriceConfiguration,
        priceCurrency: PropTypes.string,
        discountPercentage: PropTypes.number,
        isSchemaRequired: PropTypes.bool,
        label: PropTypes.string,
        variantsCount: PropTypes.number,
        mix: MixType,
        className: PropTypes.string,
        classPrice: PropTypes.string,
        classPriceValue: PropTypes.string,
        classHightPrice: PropTypes.string,
        isSmallFonts: PropTypes.bool
    };

    static defaultProps = {
        price: {},
        priceType: PRODUCT_TYPE.simple,
        originalPrice: {},
        priceCurrency: 'USD',
        discountPercentage: 0,
        isSchemaRequired: false,
        variantsCount: 0,
        mix: {},
        tierPrice: '',
        label: '',
        configuration: {},
        className: '',
        classPrice: '',
        classPriceValue: '',
        classHightPrice: '',
        isSmallFonts: false
    };

    pricePreviewRenderMap = {
        [PRODUCT_TYPE.simple]: this.renderDefaultPrice.bind(this),
        [PRODUCT_TYPE.virtual]: this.renderDefaultPrice.bind(this),
        [PRODUCT_TYPE.bundle]: this.renderBundlePrice.bind(this),
        [PRODUCT_TYPE.grouped]: this.renderGroupedPrice.bind(this),
        [PRODUCT_TYPE.downloadable]: this.renderDefaultPrice.bind(this),
        [PRODUCT_TYPE.configurable]: this.renderConfigurablePrice.bind(this)
    };

    priceLabelTypeMap = {
        [PRODUCT_TYPE.simple]: __('Starting at'),
        [PRODUCT_TYPE.virtual]: __('Starting at'),
        [PRODUCT_TYPE.bundle]: __('Starting at'),
        [PRODUCT_TYPE.grouped]: __('Starting at'),
        [PRODUCT_TYPE.downloadable]: __('Starting at'),
        [PRODUCT_TYPE.configurable]: __('As Low as')
    };

    getCurrencySchema() {
        const { isSchemaRequired, priceCurrency } = this.props;

        return isSchemaRequired ? { itemProp: 'priceCurrency', content: priceCurrency } : {};
    }

    getCurrentPriceSchema() {
        const {
            isSchemaRequired,
            variantsCount,
            price: {
                finalPrice: {
                    value: contentPrice = 0
                } = {}
            } = {}
        } = this.props;

        if (variantsCount > 1) {
            return isSchemaRequired ? { itemProp: 'lowPrice', content: contentPrice } : {};
        }

        return isSchemaRequired ? { itemProp: 'price', content: contentPrice } : {};
    }

    renderOldPrice() {
        const {
            price: {
                originalPrice: {
                    value: originalPriceValue,
                    valueFormatted: originalPriceFormatted
                } = {}
            } = {},
            discountPercentage,
            isSchemaRequired,
            variantsCount,
            classHightPrice,
            isSmallFonts
        } = this.props;

        if (discountPercentage === 0 || originalPriceValue === 0) {
            return null;
        }
        const sizeClass = isSmallFonts ? 'text-p14' : '';

        return (
            <span
                block="ProductPrice"
                elem="HighPrice"
                className={ `line-through font-normal !text-bmText-muted ${sizeClass} ${classHightPrice}` }
                aria-label={ __('Old product price') }
                itemProp={ isSchemaRequired && variantsCount > 1 ? { itemProp: 'highPrice' } : null }
            >
                { originalPriceFormatted }
            </span>
        );
    }

    renderSchema() {
        const { isSchemaRequired } = this.props;

        if (isSchemaRequired) {
            const { itemProp = null, content = null } = this.getCurrencySchema();

            return (
                <meta itemProp={ itemProp } content={ content } />
            );
        }

        return null;
    }

    renderRequiredWithChangePrice() {
        const {
            configuration: {
                containsRequiredOptionsWithPrice = false
            } = {},
            priceType
        } = this.props;

        const { [priceType]: label } = this.priceLabelTypeMap;

        return (
            <>
                { label && containsRequiredOptionsWithPrice && this.renderPriceBadge(label) }
                { this.renderDefaultPrice() }
            </>
        );
    }

    renderRegularPrice(price) {
        const {
            value,
            valueFormatted
        } = price;
        const { isSmallFonts } = this.props;

        if (!value || value <= 0 || !valueFormatted) {
            return null;
        }
        const sizeClass = isSmallFonts ? 'text-p14' : '';
        return (
            <del
            //   block="ProductPrice"
            //   elem="HighPrice"
                className={ `line-through font-normal opacity-50 inline-block me-3 ${ sizeClass }` }
            >
                { valueFormatted }
            </del>
        );
    }

    renderGroupedPrice() {
        const {
            originalPrice: {
                minFinalPrice = {},
                minFinalPriceExclTax = {}
            },
            priceType
        } = this.props;
        const { [priceType]: label } = this.priceLabelTypeMap;

        return (
            <>
                { this.renderPriceWithTax(minFinalPrice, minFinalPriceExclTax, label) }
            </>
        );
    }

    renderCustomisablePrice() {
        const {
            originalPrice: {
                minFinalPrice = {},
                minFinalPriceExclTax = {},
                minFinalPrice: { value: minValue = 0 } = {},
                maxFinalPrice: { value: maxValue = 0 } = {}
            },
            priceType
        } = this.props;

        if (minValue === maxValue) {
            return this.renderDefaultPrice();
        }

        const { [priceType]: label } = this.priceLabelTypeMap;

        return (
            <>
                { this.renderPriceWithTax(minFinalPrice, minFinalPriceExclTax, label) }
            </>
        );
    }

    renderConfigurablePrice() {
        const {
            originalPrice: {
                minFinalPrice: { value: minValue = 0 } = {},
                maxFinalPrice: { value: maxValue = 0 } = {}
            },
            configuration: {
                containsOptions = false
            } = {},
            price: {
                finalPriceExclTax = {},
                finalPrice = {}
            },
            priceType
        } = this.props;

        if (minValue === maxValue && !containsOptions) {
            return this.renderDefaultPrice();
        }

        const { [priceType]: label } = this.priceLabelTypeMap;

        return (
            <>
                { this.renderPriceWithTax(finalPrice, finalPriceExclTax, label) }
            </>
        );
    }

    renderTierPrice() {
        const {
            tierPrice,
            isSmallFonts,
            price: {
                finalPrice: {
                    valueFormatted = 0
                } = {}
            } = {}
        } = this.props;

        if (!tierPrice || tierPrice === valueFormatted) {
            return null;
        }
        const sizeClass = isSmallFonts ? 'text-p14' : '';
        return (
            <p block="ProductPrice" elem="TierPrice" className={ sizeClass }>
                { __('As low as') }
                <strong>{ ` ${tierPrice}` }</strong>
            </p>
        );
    }

    renderBundlePrice() {
        const {
            originalPrice: {
                minFinalPrice = {},
                minFinalPrice: { value: minValue = 0 } = {},
                maxFinalPrice = {},
                maxFinalPrice: { value: maxValue = 0 } = {},
                minFinalPriceExclTax = {},
                maxFinalPriceExclTax = {},
                minRegularPrice = {},
                maxRegularPrice = {},
                minRegularPrice: { value: minRegularValue = 0 } = {},
                maxRegularPrice: { value: maxRegularValue = 0 } = {}
            }
        } = this.props;

        if (minValue === maxValue) {
            const renderer = (minValue === 0)
                ? this.renderDefaultPrice()
                : this.renderPriceWithTax(minFinalPrice, minFinalPriceExclTax);

            return (
                <>
                    { minValue < minRegularValue && this.renderRegularPrice(minRegularPrice) }
                    { renderer }
                    { this.renderTierPrice() }
                </>
            );
        }

        return (
            <>
                <div
                //   block="ProductPrice"
                //   elem="BundleFrom"
                //   mods={ { hasDiscount: minValue < minRegularValue } }
                    className=""
                >
                    { minValue > 0 && this.renderPriceBadge(__('from')) }
                    { minValue < minRegularValue && this.renderRegularPrice(minRegularPrice) }
                    { this.renderPriceWithTax(minFinalPrice, minFinalPriceExclTax) }
                </div>
                <div
                //   block="ProductPrice"
                //   elem="BundleTo"
                //   mods={ { hasDiscount: maxValue < maxRegularValue } }
                    className=""
                >
                    { maxValue > 0 && this.renderPriceBadge(__('to')) }
                    { maxValue < maxRegularValue && this.renderRegularPrice(maxRegularPrice) }
                    { this.renderPriceWithTax(maxFinalPrice, maxFinalPriceExclTax) }
                </div>
            </>
        );
    }

    renderDefaultPrice(defaultLabel = null) {
        const {
            price: { finalPrice = {}, finalPriceExclTax = {} } = {},
            label
        } = this.props;

        return (
            <>
                { this.renderPriceWithTax(finalPrice, finalPriceExclTax, defaultLabel || label) }
                { this.renderSchema() }
                { this.renderOldPrice() }
            </>
        );
    }

    renderPrice(price) {
        const {
            discountPercentage,
            classPrice, classPriceValue, isSmallFonts
        } = this.props;

        const {
            value: priceValue,
            valueFormatted: priceFormatted = 0
        } = price;

        const { itemProp = null, content = null } = this.getCurrentPriceSchema();

        const PriceSemanticElementName = discountPercentage > 0 ? 'span' : 'span';

        if (priceValue === 0) {
            return <TextPlaceholder mix={ { block: 'ProductPrice', elem: 'Placeholder' } } length="custom" />;
        }

        const colorPrice = discountPercentage > 0 ? '!text-bmStatus-sale mt-0! font-bold' : 'text-bmText-default';
        const sizeClass = isSmallFonts ? 'text-p14' : '';

        return (
            <PriceSemanticElementName
                className={ `no-underline inline-flex items-baseline flex-wrap whitespace-pre ${classPrice}` }
            >
                <span
                    itemProp={ itemProp }
                    content={ content }
                    block="ProductPrice"
                    elem="PriceValue"
                    className={ `${classPriceValue} ${colorPrice} ${sizeClass}` }
                >
                    { priceFormatted }
                </span>
            </PriceSemanticElementName>
        );
    }

    renderPriceBadge(label) {
        if (!label) {
            return null;
        }

        return (
            <span
            //   block="ProductPrice"
            //   elem="PriceBadge"
                className="text-p14 font-normal me-1 whitespace-nowrap"
            >
                { label }
            </span>
        );
    }

    renderSubPrice(price) {
        const {
            value: priceExclTax = 0,
            valueFormatted: priceExclTaxFormatted = 0
        } = price;

        if (!priceExclTax) {
            return null;
        }

        return (
            <span
                aria-label={ __('Current product price excl. tax') }
            //   block="ProductPrice"
            //   elem="SubPrice"
                className="block font-normal text-p12 mt-1"
            >
                { __('Excl. tax: %s', priceExclTaxFormatted) }
            </span>
        );
    }

    renderPriceWithTax(basePrice, taxPrice, label) {
        return (
            <>
                { this.renderPrice(basePrice, label) }
            </>
        );
    }

    render() {
        const {
            price: {
                finalPrice,
                originalPrice,
                finalPrice: {
                    value: finalPriceValue = 0
                } = {}
            } = {},
            priceType,
            mix,
            className
        } = this.props;

        if (!finalPrice || !originalPrice) {
            return <SkeletonComponent items={ SKELETON_PRICE } />;
        }

        return (
            <div
            //   block="ProductPrice"
                mix={ mix }
                className={ `mb-0 align-middle text-bmText-default font-bold flex whitespace-nowrap ${className}` }
                aria-label={ `Product price: ${finalPriceValue}` }
            >
                { this.renderDefaultPrice() }
                { priceType !== PRODUCT_TYPE.bundle && this.renderTierPrice() }
            </div>
        );
    }
}

export default ProductPriceComponent;
